import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { CustomButtonProps } from '@/components/Button';
import UploadModelsIcon from '@/components/icons/UploadModels';
import BlueButtonWithIcon from '@/components/BlueButtonWithIcon';
import t from '@/utils/translate';
import BackgroundContext from '@/components/background/BackgroundContext';
import { Background, QueuedUpload } from '@/components/background/Background.class';
import { UploadStatus } from '@/state/types/model-upload';
import { styled } from '@mui/system';
import { CircularProgress } from '@mui/material';

const UploadProgress = styled(CircularProgress)({
  width: '20px',
  height: '20px',
  verticalAlign: 'middle',
  color: 'white',
});

export type UploadModelButtonProps = {
  project?: string;
} & CustomButtonProps;

type Process = {
  amount: number,
  progress: number,
  totals: Record<string, number>;
  bytes: Record<string, number>;
};

function actualize(state: Process, actual: QueuedUpload[] | null): Process {
  if (!actual) {
    return {
      amount: 0,
      progress: 0,
      totals: {},
      bytes: {},
    }
  }

  state.amount = 0;
  actual.forEach((u) => {
    if (u.status == UploadStatus.SCHEDULED || u.status === UploadStatus.INPROGRESS) {
      state.amount++;
      state.totals[u.name] = state.totals[u.name] || u.total;
      state.bytes[u.name] = u.loaded || 0;
    } else if (u.status === UploadStatus.SUCCESS) {
      if (state.totals[u.name]) {
        state.bytes[u.name] = state.totals[u.name];
      }
    } else if (u.status === UploadStatus.ABORTED || u.status === UploadStatus.FAILED) {
      delete state.totals[u.name];
      delete state.bytes[u.name];
    }
  });
  
  const total = Object.values(state.totals).reduce((s, v) => s + v, 0);
  const loaded = Object.values(state.bytes).reduce((s, v) => s + v, 0);
  return {
    ...state,
    progress: Math.min(total ? Math.round(loaded * 100 / total) : 0, 100)
  };
};

export default ({ project, ...props }: UploadModelButtonProps) => {
  const background = useContext(BackgroundContext);
  const [process, updateProcess] = useReducer(
    (state: Process, actual: QueuedUpload[] | null) => {
      return actualize(state, actual);
    },
    actualize({
      amount: 0,
      progress: 0,
      totals: {},
      bytes: {},
    }, (project && background.uploads.get(project)) || null),
  );

  const onProgress = useCallback(() => {
    if (project) {
      updateProcess(background.uploads.get(project) || null);
    }
  }, [project]);

  useEffect(() => {
    if (project) {
      background.addEventListener('uploadProgress', onProgress);
      return () => {
        background.removeEventListener('uploadProgress', onProgress);
      };  
    }
  }, [project, onProgress]);

  return (
    <BlueButtonWithIcon
      {...props}
      icon={
        (process.amount)
        ? (<UploadProgress
            style={props.disabled ? { color: 'rgba(0, 0, 0, 0.26)' } : {}}
            variant="determinate"
            value={process.progress}
            size={18}
          />)
        : (<UploadModelsIcon style={props.disabled ? { color: 'rgba(0, 0, 0, 0.26)' } : {}} />)
      }
    >
      {t('model.action.uploadModels')}
    </BlueButtonWithIcon>
  );
}
